/**
 * ClientPicker — Phase 2C.M+ (2026-09-07)
 *
 * Sélecteur partagé pour :
 *   - Dialog "Dupliquer le devis" (QuoteDuplicateDialog)
 *   - Modale "Nouveau devis depuis liste" (NewQuoteProjectModal)
 *   - Bouton "Changer de client" sur devis brouillon (QuoteEditorPage)
 *
 * Résout le bug P0 duplication devis (2026-09-07) :
 * l'ancien sélecteur cherchait des LEADS via `/api/leads?search=` — un contact
 * fraîchement créé sans projet rattaché n'apparaissait JAMAIS.
 *
 * Ce composant cherche des CONTACTS (`/api/contacts?include_projects=true`)
 * puis liste leurs projets. Si un contact n'a aucun projet, un CTA en pied
 * de dropdown permet de créer un projet vide via `POST /contacts/{id}/quick-project`.
 *
 * Vocabulaire produit user : "Contact" + "Projet" (BACKLOG "Unification
 * vocabulaire Contact/Lead/Projet"). Côté API le "projet" = `lead.id`.
 *
 * Props :
 *   open              : bool
 *   onOpenChange      : (open: bool) => void
 *   title             : string — titre du dialog (ex: "Dupliquer le devis")
 *   description       : string?
 *   confirmLabel      : string — libellé du bouton principal (ex: "Dupliquer")
 *   sourceLeadId      : string? — devis/lead source (afficher badge "Source")
 *   onSelect          : async (leadId: string) => void — appelé à la confirm
 *   submittingText    : string? — texte affiché durant l'appel (ex: "Duplication…")
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, Plus, FolderPlus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

function contactLabel(c) {
  const name = `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company_name || "(sans nom)";
  const city = c.address_city ? ` · ${c.address_city}` : "";
  return `${name}${city}`;
}

export default function ClientPicker({
  open,
  onOpenChange,
  title = "Choisir un contact",
  description = "",
  confirmLabel = "Confirmer",
  sourceLeadId = null,
  onSelect,
  submittingText = "En cours…",
}) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [expandedContactId, setExpandedContactId] = useState(null);
  const [creatingProjectFor, setCreatingProjectFor] = useState(null);

  const loadContacts = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("limit", "50");
      params.append("include_projects", "true");
      if (q.trim()) params.append("search", q.trim());
      // Tri par récence pour que les contacts fraîchement créés remontent
      params.append("sort", "-updated_at");
      const { data } = await api.get(`/contacts?${params.toString()}`);
      setContacts(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSelectedLeadId(null);
      setExpandedContactId(null);
      setSearch("");
      loadContacts("");
    }
  }, [open, loadContacts]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => loadContacts(search), 250);
    return () => clearTimeout(t);
  }, [search, loadContacts, open]);

  const handleQuickProject = async (contactId) => {
    setCreatingProjectFor(contactId);
    try {
      const { data } = await api.post(`/contacts/${contactId}/quick-project`, {
        title: "Nouveau projet",
      });
      toast.success("Projet créé sur ce contact");
      // Sélectionne d'office le projet fraîchement créé
      setSelectedLeadId(data.project_id);
      // Recharge pour afficher le nouveau projet sous ce contact
      await loadContacts(search);
      setExpandedContactId(contactId);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreatingProjectFor(null);
    }
  };

  const handleSubmit = async () => {
    if (!selectedLeadId) {
      toast.error("Sélectionnez un projet ou créez-en un nouveau");
      return;
    }
    setSubmitting(true);
    try {
      await onSelect(selectedLeadId);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        data-testid="client-picker-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un contact (nom, ville, email…)"
              className="pl-9"
              data-testid="client-picker-search"
              autoFocus
            />
          </div>

          <div
            className="max-h-80 overflow-y-auto rounded-md border border-slate-800 bg-slate-950/40"
            data-testid="client-picker-list"
          >
            {loading && contacts.length === 0 ? (
              <div className="flex items-center justify-center gap-2 p-6 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
              </div>
            ) : contacts.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">
                Aucun contact ne correspond.
              </div>
            ) : (
              contacts.map((contact) => {
                const projects = contact.projects || [];
                const isExpanded = expandedContactId === contact.id;
                const hasSource = sourceLeadId && projects.some((p) => p.id === sourceLeadId);
                return (
                  <div
                    key={contact.id}
                    className="border-b border-slate-800/60 last:border-b-0"
                    data-testid={`client-picker-contact-${contact.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => setExpandedContactId(isExpanded ? null : contact.id)}
                      className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm text-slate-300 transition-colors hover:bg-slate-800/60"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">{contactLabel(contact)}</span>
                        <span className="text-xs text-slate-500">
                          {contact.email || contact.phone || "—"}
                          {" · "}
                          {projects.length === 0
                            ? "aucun projet"
                            : `${projects.length} projet${projects.length > 1 ? "s" : ""}`}
                        </span>
                      </div>
                      {hasSource && (
                        <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] uppercase tracking-wider text-slate-400">
                          Source
                        </span>
                      )}
                    </button>

                    {isExpanded && (
                      <div className="border-t border-slate-800/60 bg-slate-950/60 px-3 py-2">
                        {projects.length > 0 ? (
                          <ul className="space-y-1">
                            {projects.map((p) => {
                              const isSelected = p.id === selectedLeadId;
                              const isSource = p.id === sourceLeadId;
                              return (
                                <li key={p.id}>
                                  <button
                                    type="button"
                                    onClick={() => setSelectedLeadId(p.id)}
                                    className={`flex w-full items-center justify-between gap-3 rounded px-2 py-1.5 text-left text-xs transition-colors ${
                                      isSelected
                                        ? "bg-[#C4553B]/20 text-slate-100"
                                        : "text-slate-400 hover:bg-slate-800/60"
                                    }`}
                                    data-testid={`client-picker-project-${p.id}`}
                                  >
                                    <span className="truncate">
                                      {p.title || "(sans titre)"}
                                    </span>
                                    <span className="text-[10px] uppercase tracking-wider text-slate-500">
                                      {isSource ? "Source" : p.status}
                                    </span>
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <p className="mb-2 text-xs italic text-slate-500">
                            Aucun projet rattaché à ce contact.
                          </p>
                        )}
                        <button
                          type="button"
                          onClick={() => handleQuickProject(contact.id)}
                          disabled={creatingProjectFor === contact.id}
                          className="mt-2 inline-flex items-center gap-1 rounded border border-dashed border-slate-700 px-2 py-1 text-[11px] text-slate-300 hover:border-[#C4553B] hover:text-[#E9A98F] disabled:opacity-50"
                          data-testid={`client-picker-quick-project-${contact.id}`}
                        >
                          {creatingProjectFor === contact.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <FolderPlus className="h-3 w-3" />
                          )}
                          Créer un projet sur ce contact
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="client-picker-cancel"
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !selectedLeadId}
            className="min-w-[140px] bg-[#C4553B] text-white hover:bg-[#A8402C]"
            data-testid="client-picker-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {submittingText}
              </>
            ) : (
              confirmLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
